import type { SvgIconTypeMap } from '@mui/material';
import { Box, Divider, MenuItem, Typography } from '@mui/material';
import type { OverridableComponent } from '@mui/material/OverridableComponent';
import type { FC, ReactElement } from 'react';
import { ActionBadge } from '~/components/atoms/ActionBadge';
import { Link } from '~/components/atoms/Link';
import { getCompoundTestIdGenerator } from '~/utils/getCompoundTestIdGenerator';
import type { IconProps } from '../Icon';

const DropdownMenuItemContent: FC<Pick<DropdownMenuItemProps, 'title' | 'IconComponent' | 'count'>> = ({
  title,
  count,
  IconComponent,
}) => {
  const getCompoundTestId = getCompoundTestIdGenerator('dropdownMenuItemContent');

  return (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
      {IconComponent && <IconComponent data-testid={getCompoundTestId('icon')} />}
      <Typography variant="body1">{title}</Typography>
      {!!count && (
        <Typography variant="body2">
          <ActionBadge label={String(count)} />
        </Typography>
      )}
    </Box>
  );
};

type CustomMenuItemProps = {
  url?: never;
  onClick?: never;
  customMenuItem?: ReactElement;
};
type LinkMenuItemProps = {
  url?: string;
  onClick?: (event: React.MouseEvent<HTMLLIElement>) => void;
  customMenuItem?: never;
};
type ButtonMenuItemProps = {
  url?: never;
  onClick?: (event: React.MouseEvent<HTMLLIElement>) => void;
  customMenuItem?: never;
};

type DropdownMenuItemProps = {
  title: string;
  IconComponent?: OverridableComponent<SvgIconTypeMap> | FC<IconProps>;
  dividerPosition?: 'above' | 'below';
  count?: number;
  onClose?: () => void;
  id?: string;
} & (CustomMenuItemProps | LinkMenuItemProps | ButtonMenuItemProps);

export const DropdownMenuItem: FC<DropdownMenuItemProps> = ({
  title,
  IconComponent,
  url,
  count,
  onClick,
  onClose,
  customMenuItem,
  dividerPosition,
  id,
}) => {
  const getCompoundTestId = getCompoundTestIdGenerator('dropdownMenuItem');

  const handleClick = (event: React.MouseEvent<HTMLLIElement>) => {
    // Close the dropdown menu if necessary when a menu item is clicked
    onClose?.();

    if (onClick) {
      onClick(event);
    }
  };

  if (customMenuItem) {
    return (
      <>
        {dividerPosition === 'above' && <Divider />}
        <MenuItem sx={{ p: 0, display: 'block' }} data-testid={getCompoundTestId('noPadding')} id={id}>
          {customMenuItem}
        </MenuItem>
        {dividerPosition === 'below' && <Divider />}
      </>
    );
  }

  return (
    <>
      {dividerPosition === 'above' && <Divider />}
      <MenuItem
        {...(url && { component: Link, to: url })}
        onClick={handleClick}
        data-testid={getCompoundTestId()}
        id={id}
      >
        <DropdownMenuItemContent title={title} IconComponent={IconComponent} count={count} />
      </MenuItem>
      {dividerPosition === 'below' && <Divider />}
    </>
  );
};
